import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { GridOptions, RowHeightParams } from "ag-grid-enterprise";
import { ColDef, GridApi, IsFullWidthRowParams } from "ag-grid-community";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  CommonModalWrapperComponent
} from "@shared/components/commons/common-modal-wrapper/common-modal-wrapper.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { ContactsService } from "../../../../../../../../shared/services/contacts.service";
import { Contact } from "@models/common/contact";
import { IndividualDto } from "@models/individuals/individual-dto";
import { IContactsChangeEvent } from "@pages/individuals/components/individuals-page/components/contacts/contacts.component";
import {
  IndividualEntityDividerComponent
} from "../../../individual-entity-divider/individual-entity-divider.component";
import {
  IEditContactModalResult
} from "@shared/components/contacts/edit-contact-modal/edit-contact-modal.component";
import {
  getContactsColDefs,
  getContactsOverlayNoRowsTemplate
} from "@pages/individuals/components/individuals-page/components/contacts/utils/get-contacts-col-defs";

export interface ISelectVerificationContactModalConfig {
  header: string;
  message: string;
}

export const SelectVerificationContactModalDefaultConfig: ISelectVerificationContactModalConfig = {
  header: 'Select Contact Information for Verification',
  message: 'Select the contact information you’d like us to use for verifying your identity and completing the document verification process.',
};

@Component({
  selector: 'app-select-verification-contact-modal',
  standalone: true,
  imports: [
    CommonModalWrapperComponent,
    LoaderComponent,
    DisclaimerComponent,
    ListGridClientSideComponent
  ],
  templateUrl: 'select-verification-contact-modal.component.html',
  styleUrl: 'select-verification-contact-modal.component.scss'
})
export class SelectVerificationContactModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private contactsService = inject(ContactsService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  @Input() individualContacts: Contact[] = [];
  @Input() individualDto: IndividualDto | null = null;

  @Output() _contactsChanged = new EventEmitter<IContactsChangeEvent>();
  @Output() confirmVerification = new EventEmitter<Contact>();

  readonly fullWidthCellRenderer = IndividualEntityDividerComponent;

  isLoading = signal<boolean>(false);
  viewContacts = signal<(Contact | string)[]>([]);
  gridApi!: GridApi;
  selectedContacts: Contact[] = [];
  config = SelectVerificationContactModalDefaultConfig;

  ngOnInit(): void {
    if (this.individualDto) {
      this.fillViewContacts(this.individualDto);
    }
  }

  confirm(): void {
    if (!this.selectedContacts.length) {
      this.toastr.error('Select a contact first.');
      return;
    }

    this.confirmVerification.emit(this.selectedContacts[0]);

    this.activeModal.close();
  }

  close(): void {
    this.activeModal.dismiss();
  }

  fillViewContacts(individual: IndividualDto): void {
    const contacts: (Contact | string)[] = [];

    if (!individual.primaryContact) {
      this.viewContacts.set(individual.contacts);
      this.setColDefs();
      this.isLoading.set(false);
      return;
    }

    const otherContacts = individual.contacts
      .filter((contact) => contact.contactId !== individual.primaryContactId);

    contacts.push('Primary Contact');
    contacts.push(individual.primaryContact);

    if (individual.contacts.length > 1) {
      contacts.push('Other Contacts');
      contacts.push(...otherContacts);
    }

    this.setColDefs();
    this.viewContacts.set(contacts);
    this.isLoading.set(false);
  }

  onAddContact(): void {
    const { instance, result } = this.contactsService.openEditContactModal();

    instance.individual = this.individualDto!;

    result.then(
      (result: IEditContactModalResult) => {
        if (result.isPrimaryContact && this.individualDto) {
          this.individualDto.primaryContactId = result.contact.contactId;
          this.individualDto.primaryContact = result.contact;
        }

        this.individualDto?.contacts.push(result.contact);
        this.fillViewContacts(this.individualDto!);

        setTimeout(() => this.selectPrimaryContact(), 200);
      },
      () => {
      }
    );
  }

  onSelectContacts(contacts: Contact[]) {
    this.selectedContacts = contacts.filter((contact) => typeof contact !== 'string');
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
    this.selectPrimaryContact();
  }

  selectPrimaryContact(): void {
    const rowIndex = this.viewContacts().findIndex((row) => typeof row !== 'string' && row.contactId && row.contactId == this.individualDto?.primaryContactId);

    if (rowIndex !== undefined) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(rowIndex);
      rowNode?.setSelected(true);

      this.onSelectContacts([this.viewContacts()[rowIndex] as Contact]);
    }
  }

  readonly gridOptions: GridOptions = { overlayNoRowsTemplate: getContactsOverlayNoRowsTemplate() };

  isFullWidthRow(params: IsFullWidthRowParams<unknown>): boolean {
    return typeof params.rowNode.data == 'string';
  }

  getRowHeight: (params: RowHeightParams) => number | undefined | null = (
    params: RowHeightParams,
  ) => {
    return typeof params.data == 'string'
      ? 24
      : 56;
  };

  setColDefs(): void {
    this.colDefs = getContactsColDefs(true);
  }

  colDefs: ColDef[] = getContactsColDefs(true);

  get contactsChangedEvent(): Observable<IContactsChangeEvent> {
    return this._contactsChanged
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef));
  }
}
